import React from "react";
import { Box, Typography, Chip, Avatar, Button, Skeleton, alpha, useTheme } from "@mui/material";
import { useLocation } from "react-router-dom";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import EditSharpIcon from "@mui/icons-material/EditSharp";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import { useGetScheduleDetailsQuery } from "../../../Api/attoDeskApi";
import { GradeSubjectsSummary } from "../../../Components/Subjects/SubjectCollection";
import { StaffProfileLink, StudentProfileLink } from "../../../Components/ProfileLinks";
import { useStudentProfilePath } from "../../StaffPortal/shared/routes";
import dayjs from "dayjs";

const initials = (name: string) =>
  (name || "").split(" ").filter(Boolean).slice(0, 2).map((n) => n[0]?.toUpperCase()).join("") || "?";

const Cell: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box>
    <Typography sx={{ fontSize: 10, fontWeight: 700, color: "text.secondary", textTransform: "uppercase", mb: 0.5 }}>{label}</Typography>
    {children}
  </Box>
);

/**
 * Compact inline expansion. Fetches the full occurrence detail lazily (same
 * endpoint as the drawer, RTK-cached) so it shows the REAL grade→subject map and
 * occurrence-level students — but stays compact and links out to the full drawer.
 */
const ExpandedScheduleDetails: React.FC<{
  row: any;
  can?: { edit?: boolean; switchStaff?: boolean; release?: boolean };
  onViewDetails: () => void;
  onAction?: (type: string) => void;
}> = ({ row, can = {}, onViewDetails, onAction }) => {
  const theme = useTheme();
  const location = useLocation();
  // Staff-role viewers get routed to /staff-portal/students/:id (StudentView);
  // admins stay on /student-history/:id (StudentHistory) — see StaffPortal/shared/routes.
  const buildStudentProfilePath = useStudentProfilePath();
  // Every profile link forwards the exact current URL so browser Back +
  // the destination page's "Back" both land the admin on the same filtered
  // Class Sessions view they came from.
  const returnUrl = location.pathname + location.search;
  const { currentData, isFetching } = useGetScheduleDetailsQuery(row?.id as any, { skip: !row?.id });
  const d: any =
    (currentData as any)?.data && String((currentData as any).data.session?.occurrence_id) === String(row?.id)
      ? (currentData as any).data
      : null;

  // Staff identity — prefer the details payload but fall back to the row so
  // the link is clickable even while the detail fetch is in flight.
  const staffId = d?.staff?.id ?? row?.staffId ?? row?.staff_id;
  const staffName = d?.staff?.name || row.staffName || "Not assigned";
  const students: Array<{ id?: number; name?: string; grade?: string | null; image?: string | null }> =
    Array.isArray(d?.students) ? d.students : [];
  const enrolled = d?.class?.enrolled ?? row.studentCount ?? students.length;
  const capacity = d?.class?.capacity;

  return (
    <Box sx={{ px: { xs: 2, md: 3 }, py: 2, bgcolor: alpha("#0f172a", 0.02) }}>
      {isFetching && !d ? (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 2 }}>
          {[0, 1, 2, 3].map((i) => (
            <Box key={i}><Skeleton width={80} height={12} /><Skeleton height={22} /></Box>
          ))}
        </Box>
      ) : (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 2 }}>
          <Cell label="Staff">
            {/* Staff link — avatar + name both route to /team-history/{staffId}.
                stopPropagation is baked into the shared component so clicking
                a name doesn't collapse the row / open the drawer / toggle the
                checkbox. Missing id falls back to plain text. */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <StaffProfileLink
                id={staffId}
                name={staffName}
                returnUrl={returnUrl}
                sx={{ display: "inline-flex", alignItems: "center", gap: 1, textDecoration: "none" }}
              >
                <Avatar sx={{ width: 26, height: 26, fontSize: 11, bgcolor: alpha(theme.palette.primary.main, 0.15), color: theme.palette.primary.main }}>{initials(staffName)}</Avatar>
                <Typography component="span" sx={{ fontSize: 12.5, fontWeight: 600 }}>
                  {staffName}
                </Typography>
              </StaffProfileLink>
            </Box>
          </Cell>

          <Cell label={`Students (${enrolled}${capacity ? ` / ${capacity}` : ""})`}>
            {/* Full student list — every student is a clickable
                StudentProfileLink → /student-history/{studentId}. The row
                used to show only a comma-joined name string, so admins
                could see enrolment count but couldn't click through to
                any specific student. Responsive grid: 1 col on xs, 2 on
                sm, 2 on md (already limited to a quarter of the row width). */}
            {students.length > 0 ? (
              <Box sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                gap: 0.75,
              }}>
                {students.map((s) => (
                  <StudentProfileLink
                    key={s.id ?? s.name}
                    id={s.id}
                    name={s.name}
                    returnUrl={returnUrl}
                    to={s.id != null ? buildStudentProfilePath(s.id) : undefined}
                    sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, textDecoration: "none", minWidth: 0 }}
                  >
                    <Avatar
                      src={s.image || undefined}
                      sx={{
                        width: 22, height: 22, fontSize: 10, fontWeight: 700,
                        bgcolor: alpha(theme.palette.success.main, 0.15),
                        color: theme.palette.success.main,
                      }}
                    >
                      {initials(s.name || "")}
                    </Avatar>
                    <Typography component="span" sx={{ fontSize: 12.5, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {s.name}
                      {s.grade ? (
                        <Box component="span" sx={{ fontSize: 10.5, fontWeight: 500, color: "text.secondary", ml: 0.5 }}>
                          · {s.grade}
                        </Box>
                      ) : null}
                    </Typography>
                  </StudentProfileLink>
                ))}
              </Box>
            ) : (
              <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                {enrolled > 0 ? `${enrolled} enrolled` : "No students assigned"}
              </Typography>
            )}
          </Cell>

          <Cell label="Class Type">
            <Typography sx={{ fontSize: 12.5 }}>{d?.class?.class_type || row.classType || "Regular"}</Typography>
          </Cell>

          <Cell label="Study Mode / Location">
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              {(d?.class?.is_online ?? row.classMode === "Online") ? <VideocamOutlinedIcon sx={{ fontSize: 14 }} /> : <LocationOnOutlinedIcon sx={{ fontSize: 14 }} />}
              <Typography sx={{ fontSize: 12.5 }}>
                {(d?.class?.is_online ?? row.classMode === "Online") ? "Online" : `In Person${d?.class?.location ? ` · ${d.class.location}` : ""}`}
              </Typography>
            </Box>
          </Cell>

          <Cell label="Subjects by Grade">
            {d
              ? <GradeSubjectsSummary groups={d.grade_subjects as any} modalTitle="Subjects by Grade" />
              : <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>—</Typography>}
          </Cell>

          <Cell label="Recurrence">
            {d?.recurrence?.is_recurring ? (
              <Typography sx={{ fontSize: 11.5 }}>
                {(d.recurrence.days || []).join(", ")} · {d.recurrence.generated_count} sessions
              </Typography>
            ) : (
              <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>One-off session</Typography>
            )}
          </Cell>

          <Cell label="Status">
            <Typography sx={{ fontSize: 12.5, textTransform: "capitalize" }}>{d?.session?.status || row.status || "scheduled"}</Typography>
          </Cell>

          <Cell label="Last Modified">
            <Typography sx={{ fontSize: 11.5 }}>
              {d?.audit?.updated_at ? dayjs(d.audit.updated_at).format("MMM D, YYYY h:mm A") : (row.lastModifiedAt ? dayjs(row.lastModifiedAt).format("MMM D, YYYY h:mm A") : "—")}
              {d?.audit?.updated_by ? ` · ${d.audit.updated_by}` : ""}
            </Typography>
          </Cell>
        </Box>
      )}

      {/* Quick actions */}
      <Box sx={{ display: "flex", gap: 1, mt: 2, flexWrap: "wrap" }}>
        <Button size="small" variant="contained" startIcon={<OpenInFullIcon />} onClick={onViewDetails} sx={{ textTransform: "none", borderRadius: "8px" }}>View Full Details</Button>
        {can.edit && <Button size="small" variant="outlined" startIcon={<EditSharpIcon />} onClick={() => onAction?.("edit")} sx={{ textTransform: "none", borderRadius: "8px" }}>Edit</Button>}
        {can.switchStaff && <Button size="small" variant="outlined" startIcon={<SwapHorizIcon />} onClick={() => onAction?.("switchStaff")} sx={{ textTransform: "none", borderRadius: "8px" }}>Switch Staff</Button>}
        {can.release && <Button size="small" variant="outlined" color="error" startIcon={<EventBusyIcon />} onClick={() => onAction?.("release")} sx={{ textTransform: "none", borderRadius: "8px" }}>Release</Button>}
      </Box>
    </Box>
  );
};

export default ExpandedScheduleDetails;
